import { CloseIcon } from "@chakra-ui/icons";
import {
  Button,
  Flex,
  Image,
  Input,
  NumberInput,
  NumberInputField,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { useAtom } from "jotai";
import { useCallback, useMemo, useState } from "react";

import { pdfRecordAtom } from "../pages/ComparePage";
import { AddPriceCommentModal } from "../compositions/modals/AddPriceCommentModal";
import { showCommentsAtom, showPricesAtom } from "../compositions/modals/ConfigureCompareViewModal";
import { hasFeature } from "../features";
import { useComparePageContext } from "../shared/state/ComparePageContext";

type Guid = ReturnType<typeof crypto.randomUUID>;

const pricingEnabled = hasFeature("pricing");

export function PriceCommentTable() {
  const { scenarios } = useComparePageContext();
  const [isRecording] = useAtom(pdfRecordAtom);
  const [showPrices] = useAtom(showPricesAtom);
  const [showComments] = useAtom(showCommentsAtom);

  // When pricing is DISABLED, we'll fall back to the legacy behavior
  const legacyShowPrices = !pricingEnabled || showPrices;
  const legacyShowComments = !pricingEnabled || showComments;

  const [priceLines, setPriceLines] = useState<Guid[]>([]);
  const [commentLines, setCommentLines] = useState<Guid[]>([]);
  const [priceOpen, setPriceOpen] = useState(true);
  const [commentOpen, setCommentOpen] = useState(true);

  const addPrice = useCallback(() => {
    setPriceLines((v) => v.concat(crypto.randomUUID()));
  }, [setPriceLines]);

  const removePrice = useCallback(
    (id: Guid) => setPriceLines((v) => v.filter((x) => x !== id)),
    [setPriceLines]
  );

  const addComment = useCallback(() => {
    setCommentLines((v) => v.concat(crypto.randomUUID()));
  }, [setCommentLines]);

  const removeComment = useCallback(
    (id: Guid) => setCommentLines((v) => v.filter((x) => x !== id)),
    [setCommentLines]
  );

  const displayedPriceLines = useMemo(
    () =>
      !isRecording
        ? priceLines
        : priceLines.filter((id) =>
            Array.from(
              document.querySelectorAll<HTMLInputElement>(`[id='${id}'] input`),
              (input) => input.value
            ).some(Boolean)
          ),
    [priceLines, isRecording]
  );

  const displayedCommentLines = useMemo(
    () =>
      !isRecording
        ? commentLines
        : commentLines.filter((id) =>
            Array.from(
              document.querySelectorAll<HTMLInputElement>(`[id='${id}'] input`),
              (input) => input.value
            ).some(Boolean)
          ),
    [commentLines, isRecording]
  );

  if (legacyShowPrices && legacyShowComments && !priceLines.length && !commentLines.length) {
    return <AddPriceCommentModal addComment={addComment} addPrice={addPrice} />;
  }

  return (
    <>
      {legacyShowPrices && !!displayedPriceLines.length && (
        <Table
          className={priceOpen ? "open" : "closed"}
          sx={{ borderSpacing: "2rem 0" }}
          variant="compare"
        >
          <Thead>
            <Tr>
              <Th onClick={() => setPriceOpen(!priceOpen)}>
                <Flex>
                  <Image
                    h={4}
                    mr={2}
                    src="/svg/chevron.svg"
                    transform={priceOpen ? "rotate(180deg)" : "rotate(0deg)"}
                    transition="transform 0.2s"
                    w={4}
                  />
                  Prijs
                </Flex>
              </Th>
              <Th>Prijs</Th>
              {scenarios.map((c) => (
                <Th key={c.id}>Prijs</Th>
              ))}
            </Tr>
          </Thead>
          <Tbody>
            {displayedPriceLines.map((key) => (
              <Tr key={key} className="prevent-hover" id={key}>
                <Td>
                  <Flex alignItems="center">
                    {!isRecording && (
                      <CloseIcon
                        cursor="pointer"
                        h={2}
                        ml={1}
                        mr={3}
                        onClick={() => removePrice(key)}
                        pointerEvents="all"
                        w={2}
                      />
                    )}
                    <Input
                      className={isRecording ? "hide-border" : ""}
                      placeholder=" "
                    />
                  </Flex>
                </Td>
                <Td>
                  <Flex mx={2}>
                    <Text lineHeight="2.6rem" mr={4}>
                      &euro;
                    </Text>{" "}
                    <NumberInput width="100%">
                      <NumberInputField
                        className={isRecording ? "hide-border" : ""}
                        placeholder=" "
                      />
                    </NumberInput>
                  </Flex>
                </Td>
                {scenarios.map((scenario) => (
                  <Td key={scenario.id}>
                    <Flex mx={2}>
                      <Text lineHeight="2.6rem" mr={4}>
                        &euro;
                      </Text>{" "}
                      <NumberInput width="100%">
                        <NumberInputField
                          className={isRecording ? "hide-border" : ""}
                          placeholder=" "
                        />
                      </NumberInput>
                    </Flex>
                  </Td>
                ))}
              </Tr>
            ))}
          </Tbody>
          {priceOpen && !isRecording && (
            <Button fontSize="xs" ml={2} onClick={addPrice} variant="link">
              Prijs toevoegen
            </Button>
          )}
        </Table>
      )}

      {legacyShowComments && (!!priceLines.length || !legacyShowPrices) && !commentLines.length && !isRecording && (
        <Button
          fontSize="xs"
          mb={4}
          ml={10}
          onClick={addComment}
          variant="link"
        >
          Opmerking toevoegen
        </Button>
      )}

      {legacyShowComments && !!displayedCommentLines.length && (
        <Table
          className={commentOpen ? "open" : "closed"}
          sx={{ borderSpacing: "2rem 0" }}
          variant="compare"
        >
          <Thead>
            <Tr>
              <Th onClick={() => setCommentOpen(!commentOpen)}>
                <Flex>
                  <Image
                    h={4}
                    mr={2}
                    src="/svg/chevron.svg"
                    transform={commentOpen ? "rotate(180deg)" : "rotate(0deg)"}
                    transition="transform 0.2s"
                    w={4}
                  />
                  Opmerking
                </Flex>
              </Th>
              <Th>Opmerking</Th>
              {scenarios.map((scenario) => (
                <Th key={scenario.id}>Opmerking</Th>
              ))}
            </Tr>
          </Thead>
          <Tbody>
            {displayedCommentLines.map((key) => (
              <Tr key={key} className="prevent-hover" id={key}>
                <Td>
                  <Flex alignItems="center">
                    {!isRecording && (
                      <CloseIcon
                        cursor="pointer"
                        h={2}
                        ml={1}
                        mr={3}
                        onClick={() => removeComment(key)}
                        pointerEvents="all"
                        w={2}
                      />
                    )}
                    <Input
                      className={isRecording ? "hide-border" : ""}
                      placeholder=" "
                    />
                  </Flex>
                </Td>
                <Td>
                  <Flex mx={2}>
                    <Input
                      className={isRecording ? "hide-border" : ""}
                      placeholder=" "
                    />
                  </Flex>
                </Td>
                {scenarios.map((scenario) => (
                  <Td key={scenario.id}>
                    <Flex mx={2}>
                      <Input
                        className={isRecording ? "hide-border" : ""}
                        placeholder=" "
                      />
                    </Flex>
                  </Td>
                ))}
              </Tr>
            ))}
          </Tbody>
          {commentOpen && !isRecording && (
            <Button fontSize="xs" ml={2} onClick={addComment} variant="link">
              Opmerking toevoegen
            </Button>
          )}
        </Table>
      )}

      {legacyShowPrices && !priceLines.length && (!!commentLines.length || !legacyShowComments) && !isRecording && (
        <Button fontSize="xs" mb={4} ml={10} onClick={addPrice} variant="link">
          Prijs toevoegen
        </Button>
      )}
    </>
  );
}
